import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Page from "../../components/page/Page.jsx";

import AddressForm from "../../components/checkout/AddressForm";
import Payment from "../../components/checkout/Payment";
import OrderSummary from "../../components/checkout/OrderSummary/OrderSummary";
import useCartStore from "../../states/CartStore";
import { fedexShippingRate } from "../../services/OnlineStore";
import styles from "./Checkout.module.css";
import SummaryContainer from "../../components/SummaryContainer/SummaryContainer.jsx";
import { LuCheckCheck, LuChevronDown, LuChevronLeft, LuChevronRight, LuChevronUp } from "react-icons/lu";

function Checkout() {
    const navigate = useNavigate();
    const cart = useCartStore((state) => state.cart);
    const [shippingRate, setShippingRate] = useState(null);
    const [shippingRateStatus, setShippingRateStatus] = useState("idle");
    const [taxAmount, setTaxAmount] = useState(0);
    const [step, setStep] = useState(1);
    const [checkoutError, setCheckoutError] = useState(null);
    const [billingAddressAutofill, setBillingAddressAutofill] = useState(true);
    const [paymentReady, setPaymentReady] = useState(false);
    const [paymentProcessing, setPaymentProcessing] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
    const [mobileSummaryExpanded, setMobileSummaryExpanded] = useState(true);
    
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 600);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const paymentRef = useRef(null);
    const cartQuantity = (cart?.items || []).reduce(
        (total, item) => total + (Number(item.quantity) || 0),
        0
    );
    const shippingQuoteReady = shippingRateStatus === "ready";

    const isAddressComplete = (address) => {
        const requiredFields = ["firstName", "lastName", "address", "city", "state", "zip", "email", "phone"];
        return requiredFields.every((field) => {
            const value = address?.[field];
            return typeof value === "string" ? value.trim().length > 0 : Boolean(value);
        });
    };


    const handleDeliveryAddressComplete = async (address = deliveryAddress) => {
        const resolvedAddress = address && typeof address === "object" && !address.target
            ? address
            : deliveryAddress;

        if (!isAddressComplete(resolvedAddress)) {
            return;
        }

        setCheckoutError(null);
        setShippingRate(null);
        setShippingRateStatus("loading");
        setBillingAddress(deliveryAddress);
        setStep(2);

        try {
            const zipCode = String(resolvedAddress?.zip || "").trim();
            const cartToken = cart?.cart_token || localStorage.getItem("cartToken");

            if (!cartToken) {
                setShippingRate(0);
                setTaxAmount(0);
                setShippingRateStatus("error");
                setCheckoutError("Your cart is not ready yet. Please add an item before checkout.");
                return;
            }

            const feeData = await fedexShippingRate(zipCode, cartQuantity || cart?.items?.length || 0, cartToken);
            setShippingRate(feeData.rate);
            setTaxAmount(feeData.tax);
            setShippingRateStatus("ready");

            console.log("Shipping quote response:", feeData);
        } catch (error) {
            console.error("Shipping quote request failed:", error);
            setShippingRate(0);
            setTaxAmount(0);
            setShippingRateStatus("error");

            if (error.status === 400) {
                setCheckoutError("Enter a valid 5-digit US ZIP code and try again.");
            } else if (error.status === 502) {
                setCheckoutError("FedEx could not return a shipping quote right now. Verify the ZIP code and try again.");
            } else {
                setCheckoutError("Shipping rates are temporarily unavailable. Please try again.");
            }
        }
    };

    // Debug function to test success page without payment
    const handleTestSuccessPage = () => {
        const mockPaymentData = {
            payment: {
                client_secret: "pi_3044Ens0MU4zdnMts1Y81SV0_secret_yHFmEEIrcFrsrUypBohPaSw8",
                payment_intent_id: "pi_3044Ens0MU4zdnMts1Y81SV0",
                cart_token: "a349b203-968f-46e3-9751-148b95f85840",
                subtotal_cents: 100,
                shipping_cents: 1575,
                total_cents: 1675
            },
            customer: {
                id: 2,
                shipping_address_id: 9,
                billing_address_id: 9,
                name: "Mich Li",
                email: "mjliao82@gmail.com",
                phone: "5854305010"
            },
            order: {
                id: 5,
                items: [
                    {
                        product_id: 1,
                        product_name: "IQ Pre-Workout",
                        quantity: 1,
                        price_cents: 100,
                        image: "https://cdn.example.com/products/preworkout/blue-front.jpg"
                    }
                ]
            }
        };
        sessionStorage.setItem("paymentData", JSON.stringify(mockPaymentData));
        navigate("/checkout/test_debug_hash/success");
    };

    const [deliveryAddress, setDeliveryAddress] = useState({
        firstName: "",
        lastName: "",
        address: "",
        city: "",
        state: "",
        zip: ""
    });

    const [billingAddress, setBillingAddress] = useState({
        firstName: "",
        lastName: "",
        address: "",
        city: "",
        state: "",
        zip: ""
    });
    const [resolvedBillingAddress, setResolvedBillingAddress] = useState(deliveryAddress);

    useEffect(() => {
        if (billingAddressAutofill) {
            setResolvedBillingAddress(deliveryAddress);
        } else {
            setResolvedBillingAddress(billingAddress);
        }
    }, [billingAddressAutofill, deliveryAddress, billingAddress]);

    const handleBillingAddressComplete = () => {
        if (!shippingQuoteReady) {
            return;
        }
        // Autofilled address was already validated as the delivery address
        if (!billingAddressAutofill && !isAddressComplete(billingAddress)) {
            return;
        }
        setStep(3);
    };


    const steps = {
        1: {
            title: "Delivery Address",
            actions: [{
                title: "Continue",
                action: handleDeliveryAddressComplete,
                style: "next",
                disabled: !isAddressComplete(deliveryAddress) || shippingRateStatus === "loading"
            }],
            completed: isAddressComplete(deliveryAddress),
        },
        2: {title: "Billing Address",
            actions: [{
                title: "Continue",
                action: handleBillingAddressComplete,
                style: "next",
                disabled: !shippingQuoteReady || (!billingAddressAutofill && !isAddressComplete(billingAddress))
            }],
            completed: billingAddressAutofill && isAddressComplete(deliveryAddress) || isAddressComplete(billingAddress),
        },
        3: {title: "Payment"}
    }


    const renderStep = (step) => {
        switch (step) {
            case 1:
                return (
                    <div className={styles.deliveryAddress}>
                        <AddressForm
                            address={deliveryAddress}
                            setAddress={setDeliveryAddress}
                        />

                    </div>

                );
            case 2:
                return (
                    <div className={styles.billingAddress}>
                        <div className={styles.billingAddressAutofillCheckboxContainer}>
                            <input
                                type="checkbox"
                                checked={billingAddressAutofill}
                                onChange={(e) => setBillingAddressAutofill(e.target.checked)}
                            />
                            <label>
                                Same as delivery address
  
                            </label>
                        </div>
                        {!billingAddressAutofill ? (
                            <AddressForm
                            address={resolvedBillingAddress}
                            setAddress={setBillingAddress}
                            />
                        ) : (
                            <div className={styles.billingAddressAutofillContainer}>
                                <p>{deliveryAddress.firstName} {deliveryAddress.lastName}</p>
                                <p>{deliveryAddress.address}</p>
                                <p>{deliveryAddress.city}, {deliveryAddress.state} {deliveryAddress.zip}</p>
                            </div>
                        )}
                    </div>

                );
            case 3:
                return (
                    <Payment
                        ref={paymentRef}
                        deliveryInfo={deliveryAddress}
                        billingInfo={resolvedBillingAddress}
                        onReadyChange={setPaymentReady}
                        onProcessingChange={setPaymentProcessing}
                        onError={setCheckoutError}
                    />
                );
            default:
                return null;
        }
    }


    function handlePreviousStep() {
        setStep((prevStep) => Math.max(prevStep - 1, 1));
    }

    const currentStepActions = steps[step]?.actions || [];

    return (
        <>
            {isMobile ? (
            <Page
                title="Checkout"
                skinny={true}
                pageStyles={{
                    content: {
                        width: "100%",
                        boxSizing: "border-box",
                        padding: "0 var(--space-3)",
                        alignItems: "stretch"
                    },
                    title: {
                        color: "var(--fg-muted)",
                        width: "100%"
                    }
                }}
            >
                <SummaryContainer
                    title="Order Summary"
                    titleIcon={mobileSummaryExpanded ? <LuChevronUp aria-hidden="true" /> : <LuChevronDown aria-hidden="true" />}
                    actionButtonTitle={paymentProcessing ? "Processing..." : "Pay Now"}
                    onAction={() => paymentRef.current?.submit()}
                    disabled={step !== 3 || !paymentReady || paymentProcessing || !shippingQuoteReady}
                    error={checkoutError}
                    collapsible={true}
                    expanded={mobileSummaryExpanded}
                    onToggle={() => setMobileSummaryExpanded((expanded) => !expanded)}
                    cartSummaryClassesOverride={{ container: styles.mobileSummary }}
                >
                    <OrderSummary
                        cart={cart}
                        shippingRate={shippingRate}
                        shippingRateStatus={shippingRateStatus}
                        taxAmount={taxAmount}
                    />
                </SummaryContainer>
                <div className={styles.checkoutContent}>
                    <div className={styles.checkoutForm}>
                        <div className={styles.stepHeaderContainer}>
                            {Object.keys(steps).map((stepTitle)=> {
                                return (
                                    <h3
                                        key={stepTitle}
                                        className={`${styles.stepHeader} ${parseInt(stepTitle) === step ? styles.active : ""}`}
                                    >
                                        {steps[stepTitle].title}
                                        <span style={{ marginLeft: "var(--space-2)" }}>{steps[stepTitle].completed && <LuCheckCheck size={20} className={styles.completedIcon}/>}</span>
                                    </h3>
                                )
                            })}
                        </div>
                        {renderStep(step)}

                        <div className={`${styles.checkoutActions} ${step === 1 ? styles.singleAction : ""}`}>
                            <div>
                                {step != 1 && (
                                    <button
                                        type="button"
                                        onClick={handlePreviousStep}
                                        className={`${styles.backButton} ${styles.actionButton}`}
                                    >
                                        <LuChevronLeft aria-hidden="true" />
                                        Previous
                                    </button>
                                )}
                            </div>
                            {currentStepActions.length > 0 && currentStepActions.map((action, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    onClick={action.action}
                                    disabled={action.disabled}
                                    className={`${styles[`${action.style}Button`]} ${styles.actionButton}`}
                                >
                                    {action.title}
                                    <LuChevronRight aria-hidden="true" />
                                </button>
                            ))}
                        </div>
                        {/* Debug button - remove after testing */}
                        {/* <button 
                            onClick={handleTestSuccessPage}
                            style={{
                                marginBottom: "20px",
                                padding: "10px 15px",
                                backgroundColor: "#ff6b6b",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontSize: "12px"
                            }}
                        >
                            🔧 Test Success Page (Debug)
                        </button> */}
                    </div>
                </div>
            </Page>
            ) : (
            <Page
                title="Checkout"
                skinny={true}
                pageStyles={{
                    title: { color: "var(--fg-muted)" }
                }}
            >
                <div className={styles.checkoutContent}>
                    <div className={styles.checkoutForm}>
                        <div className={styles.stepHeaderContainer}>
                            {Object.keys(steps).map((stepTitle)=> {
                                return (
                                    <h3
                                        key={stepTitle}
                                        className={`${styles.stepHeader} ${parseInt(stepTitle) === step ? styles.active : ""}`}
                                    >
                                        {steps[stepTitle].title}
                                        <span style={{ marginLeft: "var(--space-2)" }}>{steps[stepTitle].completed && <LuCheckCheck size={20} className={styles.completedIcon}/>}</span>
                                    </h3>
                                )
                            })}
                        </div>
                        {renderStep(step)}

                        <div className={`${styles.checkoutActions} ${step === 1 ? styles.singleAction : ""}`}>
                            <div>
                                {step != 1 && (
                                    <button
                                        type="button"
                                        onClick={handlePreviousStep}
                                        className={`${styles.backButton} ${styles.actionButton}`}
                                    >
                                        <LuChevronLeft aria-hidden="true" />
                                        Previous
                                    </button>
                                )}
                            </div>
                            {currentStepActions.length > 0 && currentStepActions.map((action, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    onClick={action.action}
                                    disabled={action.disabled}
                                    className={`${styles[`${action.style}Button`]} ${styles.actionButton}`}
                                >
                                    {action.title}
                                    <LuChevronRight aria-hidden="true" />
                                </button>
                            ))}
                        </div>
                        {/* Debug button - remove after testing */}
                        {/* <button 
                            onClick={handleTestSuccessPage}
                            style={{
                                marginBottom: "20px",
                                padding: "10px 15px",
                                backgroundColor: "#ff6b6b",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontSize: "12px"
                            }}
                        >
                            🔧 Test Success Page (Debug)
                        </button> */}
                    </div>
                    <SummaryContainer
                        title="Order Summary"
                        actionButtonTitle={paymentProcessing ? "Processing..." : "Pay Now"}
                        onAction={() => paymentRef.current?.submit()}
                        disabled={step !== 3 || !paymentReady || paymentProcessing || !shippingQuoteReady}
                        error={checkoutError}
                        cartSummaryStyles={checkoutError ? {} : {
                            actionButton: {
                                position: "absolute",
                                top: "340px",
                                left: "var(--space-3)",
                                right: "var(--space-3)",
                                width: "auto",
                                height: "46px"
                            }
                        }}
                    >
                        <OrderSummary
                            cart={cart}
                            shippingRate={shippingRate}
                            shippingRateStatus={shippingRateStatus}
                            taxAmount={taxAmount}
                        />
                    </SummaryContainer>

                </div>


            </Page>
            )}
        
        </>

    );
}

export default Checkout;